import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { CheckIcon, LightbulbIcon, PlusIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import ConfigurableTable from "../ConfigurableTable";
import { useTableSelection } from "../ConfigurableTable/useTableSelection";
import type { ChoosableChallenge } from "./types";

const ExtraContent = styled.div`
  &:empty {
    display: none !important;
  }
`;

export type ExtraContentFunctionProps = {
  challenges: Array<ChoosableChallenge>;
};

type ChallengeChooserProps = {
  forType?: string;

  createNewAction?: () => void;
  single?: boolean;
  ignores?: string[];

  trigger?: ReactElement<{ onClick?: () => void }>;
  subtitle?: string;
  confirm?: string;
  isLoading?: boolean;
  beforeContent?:
    | React.ReactNode
    | ((extraProps: ExtraContentFunctionProps) => React.ReactNode);
  afterContent?:
    | React.ReactNode
    | ((extraProps: ExtraContentFunctionProps) => React.ReactNode);

  onComplete: (challenges: Array<ChoosableChallenge>) => void;
  isOpen?: boolean;
  onClose?: () => void;
};

const ChallengeChooser = ({
  trigger,
  ignores,
  createNewAction,
  subtitle,
  isLoading,
  onComplete,
  isOpen,
  confirm,
  onClose,
  forType,
  beforeContent,
  afterContent,
  single,
}: ChallengeChooserProps) => {
  const user = useAppSelector((state) => state.user);
  const [challenges, setChallenges] = useState<ChoosableChallenge[]>([]);
  const challengeSelection = useTableSelection<ChoosableChallenge>({
    mode: single ? "single" : "multi",
    selectAll: false,
  });
  const selectedChallenges = challengeSelection.items;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(isOpen ?? false);

  const memoIgnore = useMemo(() => ignores, [ignores]);

  const [challengeSearchState, setChallengesSearchState] = useState({
    page: 1,
    limit: 10,
    query: "",
    forType: forType,
  });

  const userId = user?._id;
  const { t } = useTranslation();

  const beforeContentResolved = useMemo(() => {
    if (typeof beforeContent === "function") {
      return beforeContent({ challenges: selectedChallenges });
    }
    return beforeContent;
  }, [beforeContent, selectedChallenges]);

  const afterContentResolved = useMemo(() => {
    if (typeof afterContent === "function") {
      return afterContent({ challenges: selectedChallenges });
    }
    return afterContent;
  }, [afterContent, selectedChallenges]);

  const getChallenges = useThrottle(
    (searchState) => {
      if (!userId) {
        return;
      }
      setLoading(true);
      api.users.getChallenges(
        userId,
        searchState,
        (data) => {
          setLoading(false);
          let { challenges: newChallenges } = data;
          const ignoreList = memoIgnore ?? [];
          if (ignoreList.length > 0)
            newChallenges = newChallenges.filter(
              (c) => ignoreList.indexOf(c._id) === -1,
            );
          setChallenges(newChallenges);
        },
        () => {
          setLoading(false);
        },
      );
    },
    400,
    [userId, memoIgnore],
    { useLeadingCall: true },
  );

  const updateSearch = useCallback(
    (searchState: typeof challengeSearchState) => {
      setChallengesSearchState(searchState);
      getChallenges(searchState);
    },
    [getChallenges],
  );

  useEffect(() => {
    getChallenges({ page: 1, limit: 10, query: "", forType: forType });
  }, [getChallenges, forType]);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(!!isOpen);
    }
  }, [isOpen]);

  const finish = useCallback(() => {
    onComplete?.(selectedChallenges);

    if (isOpen === undefined) {
      setOpen(false);
    }
    // A controlled loading component should not auto-close immediately
    if (isLoading === undefined) {
      onClose?.();
    }

    challengeSelection.clear();
    setChallengesSearchState((prev) => ({
      ...prev,
      page: 1,
      limit: 10,
      query: "",
    }));
  }, [
    selectedChallenges,
    challengeSelection,
    isOpen,
    onClose,
    onComplete,
    isLoading,
  ]);

  const handleClose = useCallback(() => {
    if (isOpen === undefined) {
      setOpen(false);
    }
    onClose?.();
  }, [onClose, isOpen]);

  return (
    <>
      {trigger ? (
        isOpen === undefined ? (
          React.cloneElement(trigger, { onClick: () => setOpen(!open) })
        ) : (
          trigger
        )
      ) : isOpen === undefined ? (
        <Button size="small" onClick={() => setOpen(!open)} color="primary">
          <Icon icon={LightbulbIcon} />
          {`Choose ${t("generic.challengeWithArticle")}`}
        </Button>
      ) : null}

      <Modal open={open} onClose={handleClose}>
        <ModalHeader>
          Choose {t("generic.challengeWithArticle")}
          {subtitle && (
            <p style={{ fontSize: "1rem", fontWeight: "normal" }}>{subtitle}</p>
          )}
        </ModalHeader>
        <ModalContent scrolling>
          {beforeContentResolved ? (
            <ExtraContent>{beforeContentResolved}</ExtraContent>
          ) : null}

          <div style={{ textAlign: "right", marginBottom: 20 }}>
            <Input
              autoFocus

              placeholder={`Search for ${t("generic.challengeWithArticle")}...`}
              value={challengeSearchState.query}
              onChange={(e) => {
                challengeSelection.clear();
                updateSearch({
                  ...challengeSearchState,
                  query: e.target.value,
                });
              }}
            />
          </div>

          <ConfigurableTable
            tableKey="challenge-chooser"
            loading={loading}
            selection={challengeSelection}
            data={challenges}
            columns={[
              {
                key: "name",
                name: "Name",
                render: ({ item }) => (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ImageWithFallback
                      avatar
                      src={item.bannerImageUrl}
                      fallbackSrc={util.challengeImage()}
                    />
                    <Link
                      to={`/challenges/${item._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.name}
                    </Link>
                  </div>
                ),
              },
              {
                key: "createdAt",
                name: "Created",
                render: ({ cell }) =>
                  typeof cell === "string"
                    ? formatDate(cell, "dd/MM/yyyy")
                    : "",
              },
              ...(forType === "planner"
                ? [
                    {
                      key: "events",
                      name: "Events",
                      render: ({ item }: { item: ChoosableChallenge }) =>
                        item?.calendar?.events?.length || 0,
                    },
                  ]
                : []),
              {
                key: "stage",
                name: "Status",
                render: ({ cell }) =>
                  cell === "published" ? "Open" : "Closed",
              },
            ]}
          />
          {afterContentResolved ? (
            <ExtraContent>{afterContentResolved}</ExtraContent>
          ) : null}
        </ModalContent>
        <ModalActions>
          <Button onClick={handleClose}>Cancel</Button>
          {util.canCreateChallenges(user) &&
          challenges?.length > 0 &&
          createNewAction ? (
            <Button onClick={createNewAction} color="primary">
              <Icon icon={PlusIcon} />
              {`Create a new ${t("generic.challenge")} instead`}
            </Button>
          ) : null}
          <Button
            disabled={selectedChallenges.length === 0}
            loading={isLoading}
            onClick={finish}
            color="primary"
          >
            <Icon icon={CheckIcon} />
            {confirm || `Choose this ${t("generic.challenge")}`}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default ChallengeChooser;
